import { useState } from "react";
import { useAuth } from "../auth/AuthContext";

/**
 * Sign in with a Spoonity platform account (CONTRACT §2.4) — the same email
 * and password as the CMS and Links dashboards. Several vendors → pick one.
 * `ADMIN_AUTH_MODE=dev` adds the `fake:<email>` dev-token form below.
 */
export function SignInPage() {
  const {
    config,
    devMode,
    error,
    loading,
    selection,
    signInWithPassword,
    chooseVendor,
    cancelSelection,
    signInWithDevToken,
  } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [devEmail, setDevEmail] = useState("ops@spoonity.com");
  const [submitting, setSubmitting] = useState(false);

  if (loading || !config) {
    return (
      <div className="sign-in-screen">
        <p className="muted">Loading…</p>
      </div>
    );
  }

  const busy = async (fn: () => Promise<void>) => {
    setSubmitting(true);
    try {
      await fn();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="sign-in-screen">
      <div className="card sign-in-card">
        <h1>Spoonity Flags</h1>
        {error && (
          <p className="field error" role="alert">
            {error}
          </p>
        )}

        {selection ? (
          <div className="vendor-picker">
            <p className="muted">Your account has access to several brands. Choose one to continue.</p>
            <ul className="vendor-list" aria-label="Vendors">
              {selection.identities.map((vendor) => (
                <li key={vendor.id}>
                  <button
                    className="btn vendor-choice"
                    type="button"
                    disabled={submitting}
                    onClick={() => busy(() => chooseVendor(vendor.id))}
                  >
                    <span>{vendor.name}</span>
                    <span className={`role-badge ${vendor.role}`}>{vendor.role}</span>
                  </button>
                </li>
              ))}
            </ul>
            <button className="btn small" type="button" onClick={cancelSelection}>
              Use a different account
            </button>
          </div>
        ) : (
          <>
            <p className="muted">Sign in with your Spoonity account.</p>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void busy(() => signInWithPassword(email.trim(), password));
              }}
            >
              <div className="field">
                <label htmlFor="sign-in-email">Email</label>
                <input
                  id="sign-in-email"
                  type="email"
                  autoComplete="username"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@brand.com"
                />
              </div>
              <div className="field">
                <label htmlFor="sign-in-password">Password</label>
                <input
                  id="sign-in-password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
              <button className="btn primary" type="submit" disabled={submitting || !email || !password}>
                Sign in
              </button>
            </form>
          </>
        )}

        {devMode && !selection && (
          <form
            className="dev-sign-in"
            onSubmit={(e) => {
              e.preventDefault();
              void busy(() => signInWithDevToken(devEmail));
            }}
          >
            <hr />
            <div className="field">
              <label htmlFor="dev-token-email">Dev token (email)</label>
              <input
                id="dev-token-email"
                type="text"
                value={devEmail}
                onChange={(e) => setDevEmail(e.target.value)}
                placeholder="you@spoonity.com"
              />
              <span className="hint">
                Local dev only (<code>ADMIN_AUTH_MODE=dev</code>): the service accepts
                <code> fake:&lt;email&gt;</code>.
              </span>
            </div>
            <button className="btn" type="submit" disabled={submitting || !devEmail}>
              Continue
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
